import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ModalTypes } from '../components/modals/contants/modal-types.enum';
import { EmitterTypes } from '../constants/emitter-types.enum';
import { TunnelResult } from '../classes/tunnel-result';

import { BasicModalLoaderPayload } from '../classes/basic-modal-loader-payload';
import { ConfirmModalLoaderPayload } from '../classes/confirm-modal-loader-payload';
import { PlaceholderModalLoaderPayload } from '../classes/placeholder-modal-loader-payload';

export interface IModalLoaderMessage {
  componentToLoad: any;
  payload: BasicModalLoaderPayload | ConfirmModalLoaderPayload | PlaceholderModalLoaderPayload | any; // for backward compatibility
  modalType: ModalTypes;
  emitter: EmitterTypes;
}

@Injectable()
export class DynamicModalLoaderService {

  subscribeList: Map<string, BehaviorSubject<TunnelResult | IModalLoaderMessage>> = new Map<string, BehaviorSubject<TunnelResult | IModalLoaderMessage>>();

  constructor() {
  }

  /**
   * Register new observable
   * @param name
   * @returns {Observable<IModalLoaderMessage>}
   */
  registerObservable(name): Observable<IModalLoaderMessage> {
    if (!this.subscribeList.has(name)) {
      const observable = <BehaviorSubject<IModalLoaderMessage | TunnelResult>> new BehaviorSubject(null);
      this.subscribeList.set(name, observable);
      return observable.asObservable() as Observable<IModalLoaderMessage>;
    } else {
      return this.subscribeList.get(name).asObservable() as Observable<IModalLoaderMessage>;
    }
  }

  /**
   * returning the observable of the popup
   * optional - register an observable if not found
   *
   * @param {string} name
   * @param {boolean} shouldCreateObservable
   * @return {any}
   */
  getObservable(name: string, shouldCreateObservable: boolean = false) {
    if (!this.subscribeList.has(name)) {
      if (shouldCreateObservable) {
        this.registerObservable(name);
      } else {
        return null;
      }
    }

    return this.subscribeList.get(name);
  }

  /**
   * returns Observable to subscribe to
   * @param componentToLoad - component instance
   * @param {ModalTypes} modalType
   * @param name
   * @param payload
   * @returns {Observable<TunnelResult> }
   */
  popupModal(
    componentToLoad: any,
    payload?: any,
    // BasicModalLoaderPayload|ConfirmModalLoaderPayload|PlaceholderModalLoaderPayload,
    modalType: ModalTypes = ModalTypes.NONE,
    name: string = 'DYNAMIC_MODAL_LOADER'
  ): Observable<TunnelResult> {
    let observable;

    if (!this.subscribeList.has(name)) {
      return null;
    } else {
      observable = this.subscribeList.get(name);
    }

    observable.next({
      componentToLoad,
      payload,
      modalType,
      emitter: EmitterTypes.loader
    });

    return observable.asObservable().filter(res => !res.hasOwnProperty('emitter') || res.emitter === EmitterTypes.parent);
  }

  /**
   * Emit next to the given observable
   * @param name
   * @param payload
   * @param shouldDismiss
   */
  next(name: string, payload: TunnelResult, shouldDismiss: boolean = false) {
    const observable = this.subscribeList.get(name);
    observable.next(payload);

    if (shouldDismiss) {
      this.dismissObservable(name);
    }
  }

  /**
   * Dismiss observable and remove it from subscribe list
   * @param name
   * @param shouldEmit
   * @param {TunnelResult} payload
   */
  dismissObservable(name: string, shouldEmit: boolean = false, payload?: TunnelResult) {
    name = name ? name : 'DYNAMIC_MODAL_LOADER';
    if (!this.subscribeList.has(name)) {
      return;
    }

    const observable = this.subscribeList.get(name);

    if (observable) {
      this.subscribeList.delete(name);

      if (shouldEmit) {
        observable.next(payload);
      }

      observable.complete();
    }
  }
}

